import { environment } from '@telespot/shared/environment';
import smartlookClient from 'smartlook-client';

import { Recorder, Variables } from '../models/recorder';

export type SmartlookClient = Pick<
  typeof smartlookClient,
  'init' | 'initialized' | 'pause' | 'resume' | 'identify' | 'disable' | 'consentAPI' | 'consentIP' | 'consentForms'
>;

type SmartlookProperties = Parameters<SmartlookClient['identify']>[1];

export class Smartlook implements Recorder {
  private disabled = false;

  constructor(private readonly client: SmartlookClient = smartlookClient) {}

  /**
   * The recorder script is fetched on the first start, so nothing is loaded until recording is
   * wanted. init() is one-shot, hence resume() for every later start.
   */
  public start(consented: boolean): void {
    if (this.disabled || !environment.recorder?.enable) return;

    if (this.client.initialized()) this.client.resume();
    else this.client.init(environment.recorder.apiKey);

    this.declareConsents(consented);
  }

  public stop(): void {
    if (!this.client.initialized()) return;

    this.client.pause();
  }

  public identify(uid: string, vars: Variables): void {
    this.client.identify(uid, vars as SmartlookProperties);
  }

  /** Terminal: a later consent change must not undo it. */
  public disable(): void {
    this.disabled = true;
    this.client.disable();
  }

  /**
   * Re-declared on every start: a session recorded without a grant captures neither form input,
   * IP nor API identity, and a grant that arrives mid-session upgrades it.
   */
  private declareConsents(granted: boolean): void {
    this.client.consentAPI(granted ? 'API consent granted' : false);
    this.client.consentIP(granted ? 'IP consent granted' : false);
    this.client.consentForms(granted ? 'Forms consent granted' : false);
  }
}
